/**
 * ProjectChat — permanent AI conversation thread for a project.
 * Shows history + post new question + share-with-client button.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Send, Loader2, Share2, Copy, Bot, User as UserIcon, Trash2, ArrowLeft,
} from "lucide-react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";

export default function ProjectChat() {
  const { projectId } = useParams();
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const navigate = useNavigate();
  const [projectName, setProjectName] = useState("");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareInfo, setShareInfo] = useState(null);
  const [allowPublicPost, setAllowPublicPost] = useState(true);
  const endRef = useRef(null);

  const loadMessages = async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/chat/messages`);
      setMessages(data.messages || []);
      setProjectName(data.project_name || "");
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { loadMessages(); /* eslint-disable-next-line */ }, [projectId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || loading) return;
    setLoading(true);
    // Optimistic insert
    const tempId = `tmp-${Date.now()}`;
    setMessages(m => [...m, { id: tempId, role: "user", text: q, author_name: "me", created_at: new Date().toISOString(), _pending: true }]);
    setInput("");
    try {
      const { data } = await api.post(`/projects/${projectId}/chat/messages`, {
        text: q, language: es ? "es" : "en",
      });
      setMessages(m => [
        ...m.filter(x => x.id !== tempId),
        data.user_message, data.ai_message,
      ]);
    } catch (e) {
      setMessages(m => m.filter(x => x.id !== tempId));
      alert(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const deleteMsg = async (id) => {
    if (!window.confirm(es ? "¿Borrar mensaje?" : "Delete message?")) return;
    try {
      await api.delete(`/projects/${projectId}/chat/messages/${id}`);
      setMessages(m => m.filter(x => x.id !== id));
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    }
  };

  const createShareLink = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/chat/share-link`, {
        expires_days: 30,
        allow_public_questions: allowPublicPost,
      });
      setShareInfo(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Error");
    } finally { setSharing(false); }
  };

  const copyShare = async () => {
    if (!shareInfo?.url) return;
    try { await navigator.clipboard.writeText(shareInfo.url); alert(es?"Link copiado":"Link copied"); }
    catch { window.prompt(es?"Copia el link:":"Copy the link:", shareInfo.url); }
  };

  return (
    <div className="max-w-4xl mx-auto p-6" data-testid="project-chat-page">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(-1)}
          className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 text-sm"
          data-testid="chat-back-btn">
          <ArrowLeft size={14} /> {es ? "Volver" : "Back"}
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-400">
          Project chat
        </div>
      </div>

      {/* Header + share controls */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm mb-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="chat-project-name">
              {projectName || "—"}
            </h1>
            <p className="text-xs text-slate-500 mt-1">
              {es ? "Pregúntale a la IA sobre este plano. Las respuestas se guardan para siempre."
                  : "Ask the AI about this plan. Answers are saved forever."}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-xs text-slate-600 inline-flex items-center gap-1.5">
              <input type="checkbox" checked={allowPublicPost}
                     onChange={e => setAllowPublicPost(e.target.checked)}
                     data-testid="allow-public-post" className="accent-[#8B5CF6]" />
              {es ? "Cliente puede preguntar" : "Client can ask"}
            </label>
            <button onClick={createShareLink} disabled={sharing}
              data-testid="create-chat-share-btn"
              className="bg-[#8B5CF6] text-white hover:bg-[#7C3AED] rounded-xl px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-60">
              {sharing ? <Loader2 size={12} className="animate-spin" /> : <Share2 size={12} />}
              {es ? "Compartir" : "Share"}
            </button>
          </div>
        </div>
        {shareInfo && (
          <div className="mt-3 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-xl p-2.5 flex items-center gap-2" data-testid="chat-share-box">
            <input readOnly value={shareInfo.url}
                   className="flex-1 bg-white border border-[#8B5CF6]/20 rounded-lg px-2 py-1 text-xs font-mono text-slate-700" />
            <button onClick={copyShare} data-testid="copy-chat-share"
              className="bg-[#8B5CF6] text-white rounded-lg px-2.5 py-1 text-xs inline-flex items-center gap-1">
              <Copy size={11} /> {es ? "Copiar" : "Copy"}
            </button>
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#6D28D9]">
              {shareInfo.days}d · {shareInfo.allow_public_questions ? (es?"2 vías":"2-way") : (es?"Solo leer":"Read-only")}
            </span>
          </div>
        )}
      </div>

      {/* Messages */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="max-h-[60vh] overflow-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400" data-testid="chat-empty">
              {es ? "Aún no hay preguntas. Empieza abajo." : "No questions yet. Start below."}
            </div>
          ) : messages.map(m => {
            const isUser = m.role === "user";
            return (
              <div key={m.id}
                   data-testid={`chat-msg-${m.role}`}
                   className={`flex gap-2 ${isUser ? "justify-end" : "justify-start"}`}>
                {!isUser && (
                  <div className="w-7 h-7 rounded-full bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center justify-center shrink-0">
                    <Bot size={13} />
                  </div>
                )}
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                  isUser ? "bg-[#003366] text-white" : "bg-slate-100 text-slate-800"
                }`}>
                  <div className="text-[10px] font-mono uppercase tracking-wider opacity-70 mb-1 flex items-center gap-1.5">
                    <span>{m.author_name}</span>
                    {m.source === "public" && (
                      <span className="bg-white/20 rounded-full px-1.5 py-0.5 text-[9px]">EXT</span>
                    )}
                    <span className="opacity-60">· {new Date(m.created_at).toLocaleString(es?"es-ES":"en-US")}</span>
                  </div>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                </div>
                {isUser && (
                  <>
                    <div className="w-7 h-7 rounded-full bg-[#003366]/10 text-[#003366] flex items-center justify-center shrink-0">
                      <UserIcon size={13} />
                    </div>
                  </>
                )}
                {!m._pending && (
                  <button onClick={() => deleteMsg(m.id)}
                    className="self-start text-slate-300 hover:text-red-600 p-1"
                    data-testid={`delete-msg-${m.id}`}
                    title={es ? "Borrar" : "Delete"}>
                    <Trash2 size={11} />
                  </button>
                )}
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="border-t border-slate-100 p-3 flex gap-2">
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && send()}
            placeholder={es ? "¿Cuántas cámaras hay en el piso 2?" : "How many cameras on floor 2?"}
            disabled={loading}
            data-testid="chat-input"
            className="flex-1 border border-slate-300 rounded-xl px-3 py-2 text-sm" />
          <button onClick={send} disabled={loading || !input.trim()}
            data-testid="chat-send-btn"
            className="bg-[#8B5CF6] text-white rounded-xl px-4 py-2 hover:bg-[#7C3AED] disabled:opacity-50 inline-flex items-center gap-1.5 text-sm font-medium">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {es ? "Enviar" : "Send"}
          </button>
        </div>
      </div>
    </div>
  );
}
